/**
 * useSurfaceSplitStore — persisted split configuration for wall faces that
 * have been divided into two material zones (e.g. lower bathroom tiles /
 * upper plaster, or a left/right split).
 *
 * Only stores WHERE the split line is; the two resulting mesh names
 * (`${baseMeshName}__a` / `__b`, see surfaceSplitUtils.ts) each carry their
 * own independent AppliedMaterial entry in useMaterialStore, exactly like
 * any other named mesh — splitting doesn't need its own material storage.
 */

import { create } from 'zustand';

/**
 * "horizontal" = the two parts sit side by side (left/right) — the split
 * happens ALONG the horizontal axis. "vertical" = the two parts stack
 * (lower/upper). See surfaceSplitUtils.ts's isLeftRightSplit/
 * isLowerUpperSplit — always go through those rather than comparing this
 * string directly, so the convention can't get flipped by accident.
 */
export type SplitAxis = 'horizontal' | 'vertical';

export interface SurfaceSplitConfig {
  axis: SplitAxis;
  /**
   * Distance in meters from the face-local origin to the split line —
   * measured from the face's start corner for a left/right split
   * ('horizontal'), or from the bottom edge for a lower/upper split
   * ('vertical'; so this is "wainscot height").
   */
  position: number;
}

/** baseMeshName (unsplit mesh name) → SurfaceSplitConfig */
export type SurfaceSplitMap = Record<string, SurfaceSplitConfig>;

export interface SerializedSurfaceSplit extends SurfaceSplitConfig {
  meshName: string;
}

interface SurfaceSplitState {
  /** objectId (instance id, e.g. '__floorplan__') → baseMeshName → SurfaceSplitConfig */
  splits: Record<string, SurfaceSplitMap>;

  /**
   * True when a split has been added/changed/removed (via setSplit/
   * removeSplit — NOT initSplits, which is a scene-load baseline, not a
   * user edit) since the last successful save or scene load. A wall-face
   * split is a real change to the wall's mesh geometry (extra sub-meshes),
   * so SceneView.tsx's save flow reads this to decide whether the
   * architecture GLBs need re-exporting — Blueprint3D's own floorplan
   * dirty flag (floorplanSaveBridge.ts) never sees split edits since they
   * don't touch the 2D floorplan model at all.
   */
  isDirty: boolean;

  setSplit(objectId: string, baseMeshName: string, config: SurfaceSplitConfig): void;
  removeSplit(objectId: string, baseMeshName: string): void;
  getSplitsForObject(objectId: string): SurfaceSplitMap;
  getSplit(objectId: string, baseMeshName: string): SurfaceSplitConfig | undefined;

  /** Returns the split list for a given object, as expected by the scene-save payload. */
  serializeSplits(objectId: string): SerializedSurfaceSplit[];

  /** Bulk-import saved splits when loading a scene. Does not affect isDirty — call markClean() separately on a genuine scene switch. */
  initSplits(objectId: string, savedList: SerializedSurfaceSplit[]): void;

  /** Mark clean after a successful save, or after loading a fresh scene. */
  markClean(): void;

  /**
   * Drop every split, for every object, and mark clean — call when a different
   * scene is loaded (see utils/resetSceneState.ts). initSplits no-ops on an
   * empty list, so without this a new scene kept the previous one's wainscot
   * lines: splits are keyed by the CONSTANT FLOORPLAN_OBJECT_ID, one shared
   * entry across every floorplan scene.
   */
  reset(): void;
}

export const useSurfaceSplitStore = create<SurfaceSplitState>((set, get) => ({
  splits: {},
  isDirty: false,

  setSplit(objectId, baseMeshName, config) {
    set((state) => ({
      splits: {
        ...state.splits,
        [objectId]: {
          ...(state.splits[objectId] ?? {}),
          [baseMeshName]: config,
        },
      },
      isDirty: true,
    }));
  },

  removeSplit(objectId, baseMeshName) {
    set((state) => {
      const forObj = { ...(state.splits[objectId] ?? {}) };
      delete forObj[baseMeshName];
      return {
        splits: {
          ...state.splits,
          [objectId]: forObj,
        },
        isDirty: true,
      };
    });
  },

  getSplitsForObject(objectId) {
    return get().splits[objectId] ?? {};
  },

  getSplit(objectId, baseMeshName) {
    return get().splits[objectId]?.[baseMeshName];
  },

  serializeSplits(objectId) {
    const map = get().splits[objectId] ?? {};
    return Object.entries(map).map(([meshName, config]) => ({ meshName, ...config }));
  },

  initSplits(objectId, savedList) {
    if (!savedList?.length) return;
    const map: SurfaceSplitMap = {};
    for (const item of savedList) {
      if (item.meshName) map[item.meshName] = { axis: item.axis, position: item.position };
    }
    set((state) => ({
      splits: {
        ...state.splits,
        [objectId]: map,
      },
    }));
  },

  markClean() {
    set({ isDirty: false });
  },

  reset() {
    set({ splits: {}, isDirty: false });
  },
}));
